import { createContext, useContext, useEffect, useState, useSyncExternalStore, type ReactNode } from 'react';
import { Center, Loader } from '@mantine/core';
import Dexie from 'dexie';
import * as core from '@flow/core';
import type { AppError, Deps, Result } from '@flow/core';
import { notifications } from '@mantine/notifications';
import { accountDbName, FlowDb } from '../infra/db';
import { createDexieDeps, createRecorder } from '../infra/dexieStores';
import { createSyncEngine, type SyncEngine, type SyncStatus } from '../infra/sync';
import { useAccount } from '../features/account/AccountProvider';

/** Every use case, pre-wired with its dependencies. The UI only ever calls these. */
const bind = (d: Deps) => ({
  createTournament: core.createTournament(d),
  renameTournament: core.renameTournament(d),
  updateTournament: core.updateTournament(d),
  setTournamentDates: core.setTournamentDates(d),
  setTournamentFormat: core.setTournamentFormat(d),
  changeRoundFormat: core.changeRoundFormat(d),
  deleteTournament: core.deleteTournament(d),
  createRound: core.createRound(d),
  importAssignment: core.importAssignment(d),
  updateRoundDetails: core.updateRoundDetails(d),
  recordCoinFlip: core.recordCoinFlip(d),
  setResolution: core.setResolution(d),
  setSides: core.setSides(d),
  setRoundStatus: core.setRoundStatus(d),
  deleteRound: core.deleteRound(d),
  purgeExpiredNames: core.purgeExpiredNames(d),
  addRow: core.addRow(d),
  ensureRows: core.ensureRows(d),
  ensureStarterRows: core.ensureStarterRows(d),
  writeCell: core.writeCell(d),
  toggleMark: core.toggleMark(d),
  deleteRow: core.deleteRow(d),
  setArgumentTitle: core.setArgumentTitle(d),
  saveRoundSummary: core.saveRoundSummary(d),
  loadTimer: core.loadTimer(d),
  timerCommand: core.timerCommand(d),
  timerUndo: core.timerUndo(d),
  loadBallot: core.loadBallot(d),
  saveBallot: core.saveBallot(d),
  saveSpeakerNote: core.saveSpeakerNote(d),
  commitDecision: core.commitDecision(d),
  reopenDecision: core.reopenDecision(d),
  markSubmitted: core.markSubmitted(d),
  exportBackup: core.exportBackup(d),
  importBackup: core.importBackup(d),
});

export type UseCases = ReturnType<typeof bind>;

interface Services {
  db: FlowDb;
  deps: Deps;
  uc: UseCases;
  sync: SyncEngine;
}

/** Composition root: the only place that knows IndexedDB is the storage. One database per account. */
export const createServices = (accountId: string, auth: () => Promise<Record<string, string> | null>): Services => {
  const db = new FlowDb(accountDbName(accountId));
  const sync = createSyncEngine({ db, accountId, auth });
  const deps = createDexieDeps(db, createRecorder(db, accountId, () => sync.changed()));
  return { db, deps, uc: bind(deps), sync };
};

/**
 * Before accounts, everything lived in one database named "flow". The first account to sign in on this browser
 * takes it over (copied into its own database, then synced from there); the old database is left in place untouched.
 * It's never copied into an account that already has tournaments, and never twice.
 */
const LEGACY = 'flow';
const CLAIMED = 'flow.legacyClaimedBy';
export const claimLegacy = async (db: FlowDb, accountId: string) => {
  try {
    if (localStorage.getItem(CLAIMED) || !(await Dexie.exists(LEGACY)) || (await db.tournaments.count()) > 0) return;
  } catch {
    return;
  }
  const old = new Dexie(LEGACY);
  old.version(1).stores({ tournaments: 'id, updatedAt', rounds: 'id, tournamentId, updatedAt', names: 'roundId, purgeAfter', rows: 'id, roundId, sheetId', cells: 'id, roundId, rowId', timers: 'roundId', ballots: 'roundId', settings: 'id' });
  try {
    await old.open();
    const tables = ['tournaments', 'rounds', 'names', 'rows', 'cells', 'timers', 'ballots', 'settings'] as const;
    const data = await Promise.all(tables.map((t) => old.table(t).toArray()));
    await db.transaction('rw', db.everything, async () => {
      for (const [i, t] of tables.entries()) await db.table(t).bulkPut(data[i]!);
    });
    localStorage.setItem(CLAIMED, accountId);
  } finally {
    old.close();
  }
};

const Ctx = createContext<Services | null>(null);

/** The signed-in account's data and sync. Everything inside the gate uses it. */
export const AccountServices = ({ accountId, children }: { accountId: string; children: ReactNode }) => {
  const { authHeaders } = useAccount();
  const [services, setServices] = useState<Services | null>(null);
  useEffect(() => {
    let live = true;
    const s = createServices(accountId, authHeaders);
    void claimLegacy(s.db, accountId).catch(() => undefined).then(() => {
      if (!live) return;
      setServices(s);
      void s.sync.run();
    });
    return () => {
      live = false;
      s.sync.stop();
      s.db.close();
      setServices(null);
    };
  }, [accountId, authHeaders]);
  if (!services) return <Center mih="100dvh"><Loader size="sm" /></Center>;
  return <Ctx.Provider value={services}>{children}</Ctx.Provider>;
};

/** For tests and tools: services you built yourself. */
export const ServicesProvider = ({ services, children }: { services: Services; children: ReactNode }) => (
  <Ctx.Provider value={services}>{children}</Ctx.Provider>
);

export const useServices = (): Services => {
  const s = useContext(Ctx);
  if (!s) throw new Error('ServicesProvider missing');
  return s;
};

const OFF: SyncStatus = { state: 'off', pending: 0, lastSyncAt: null, message: null, rejected: [] };
const noop = () => () => {};
/** Sync status for this account (null outside the app, e.g. on the front page). */
export const useSyncStatus = (): { status: SyncStatus; syncNow: () => Promise<void> } | null => {
  const s = useContext(Ctx);
  const status = useSyncExternalStore(s ? s.sync.subscribe : noop, s ? s.sync.getStatus : () => OFF);
  return s ? { status, syncNow: s.sync.run } : null;
};

export const useUseCases = () => useServices().uc;
export const useDeps = () => useServices().deps;

/** Show a friendly toast for an error that isn't tied to a form field. */
export const reportError = (e: AppError) => {
  notifications.show({
    color: 'red',
    title: e.kind === 'validation' ? 'Please check your input' : "Couldn't do that",
    message: e.kind === 'validation' && e.issues.length > 1 ? e.issues.map((i) => i.message).join(' ') : e.message,
  });
};

/** Await a use case; toast on failure; return the value or undefined. */
export const run = async <T,>(p: Promise<Result<T>>): Promise<T | undefined> => {
  const r = await p;
  if (r.ok) return r.value;
  reportError(r.error);
  return undefined;
};
